/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.data.util.BusinessError
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.model.profile.OnboardingStep
import studio.oddpods.cauce.core.model.profile.Profile
import studio.oddpods.cauce.core.network.auth.AuthService

class ProfileRepositoryImpl(
    private val supabase: SupabaseClient,
    private val authService: AuthService,
) : ProfileRepository {

    private val userId: String
        get() = authService.currentUserId
            ?: throw BusinessError.NotAuthenticated

    override suspend fun getCurrentProfile(): Result<Profile> = safeCall {
        val dto = supabase.postgrest.from("profiles").select {
            filter { eq("id", userId) }
        }.decodeSingle<ProfileDto>()
        dto.toDomain()
    }

    override suspend fun updateDisplayNameAndFee(displayName: String, defaultFeeCentavos: Long): Result<Unit> =
        safeCall {
            supabase.postgrest.from("profiles").update({
                set("display_name", displayName)
                set("default_session_fee", defaultFeeCentavos)
                set("onboarding_step", OnboardingStep.ADD_PATIENT.serialName)
            }) {
                filter { eq("id", userId) }
            }
        }

    override suspend fun setOnboardingStep(step: OnboardingStep): Result<Unit> = safeCall {
        supabase.postgrest.from("profiles").update({
            set("onboarding_step", step.serialName)
        }) {
            filter { eq("id", userId) }
        }
    }

    override suspend fun completeOnboarding(): Result<Unit> = safeCall {
        supabase.postgrest.from("profiles").update({
            set("onboarding_step", OnboardingStep.COMPLETED.serialName)
            set("onboarding_completed", true)
        }) {
            filter { eq("id", userId) }
        }
    }

    override suspend fun updateReminderSettings(
        scheduleDays: List<Int>,
        maxReminders: Int,
        template: String,
    ): Result<Unit> = safeCall {
        supabase.postgrest.from("profiles").update({
            set("reminder_schedule_days", scheduleDays)
            set("max_reminders", maxReminders)
            set("reminder_template", template)
        }) {
            filter { eq("id", userId) }
        }
    }
}
